// Her live call on the dashboard (WEB-07): while a call on her phone rings or goes on, a card names the customer as
// her phone did (D-094), with one click to open them when she may. Nothing opens by itself. Hide puts the card away
// for this call only. If she asks, a browser notification also says so while this tab is in the background; clicking
// it opens the customer.
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { liveCallView } from "../lib/liveCall";
import type { LiveCall } from "../lib/types";
import { useSession } from "./session";

const HIDDEN_KEY = "voxowa.liveCall.hidden";
const NOTIFY_KEY = "voxowa.liveCall.notify";

function read(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: string): void {
  try {
    storage().setItem(key, value);
  } catch {
    // Storage refused (private window): the choice lasts until the page reloads.
  }
}

const canNotify = () => typeof window !== "undefined" && "Notification" in window;

export function LiveCallCard({ memberId }: { memberId: string }) {
  const { api } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [call, setCall] = useState<LiveCall | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [, setTick] = useState(0);
  const [hidden, setHidden] = useState<string | null>(() => read(() => sessionStorage, HIDDEN_KEY));
  const [notify, setNotify] = useState(
    () => canNotify() && Notification.permission === "granted" && read(() => localStorage, NOTIFY_KEY) === "1",
  );
  // The call the notification last spoke about; undefined until the first answer, so the call already going on when
  // the page opened is never announced.
  const seen = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api.liveCall().then(
        (c) => {
          if (!alive) return;
          setCall(c);
          setLoaded(true);
        },
        () => alive && setLoaded(true),
      );
    };
    load();
    const stop = api.onLiveCall(memberId, load);
    // Back to the tab: catch up on anything missed while the browser slept.
    const onVisible = () => !document.hidden && load();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      stop();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, memberId]);

  const view = liveCallView(call, new Date());
  const key = view?.key ?? null;

  // The clock on the card moves while a call goes on.
  useEffect(() => {
    if (!key) return;
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, [key]);

  // A new call while this tab is in the background: a notification, if she asked for them.
  useEffect(() => {
    if (!loaded) return;
    if (seen.current === undefined || key === seen.current) {
      seen.current = key;
      return;
    }
    seen.current = key;
    if (!key || !view || !notify || !document.hidden) return;
    const leadId = view.leadId;
    const n = new Notification(`${view.eyebrow.split(" · ")[0]}: ${view.name}`, {
      body: leadId ? "Click to open the customer." : view.lines.join(" · "),
      tag: "voxowa-live-call",
    });
    n.onclick = () => {
      window.focus();
      if (leadId) navigate(`/leads/${leadId}`);
      n.close();
    };
  }, [loaded, key, view, notify, navigate]);

  if (!view || hidden === view.key) return null;
  const path = view.leadId ? `/leads/${view.leadId}` : null;
  const here = path !== null && location.pathname === path;

  return (
    <div className="live-call" role="status" aria-live="polite">
      <div className="live-call-eyebrow">
        <span className="live-dot" aria-hidden /> {view.eyebrow}
      </div>
      <div className="live-call-name">{view.name}</div>
      {view.lines.map((line) => <div key={line} className="live-call-line">{line}</div>)}
      <div className="live-call-actions">
        {path && (here
          ? <span className="muted small">This customer is open</span>
          : <button onClick={() => navigate(path)}>Open customer</button>)}
        <button
          className="link small"
          onClick={() => {
            setHidden(view.key);
            write(() => sessionStorage, HIDDEN_KEY, view.key);
          }}
        >
          Hide
        </button>
      </div>
      {canNotify() && !notify && Notification.permission !== "denied" && (
        <button
          className="link small"
          onClick={() =>
            void Notification.requestPermission().then((p) => {
              if (p !== "granted") return;
              write(
                () =>
                  localStorage,
                NOTIFY_KEY,
                "1",
              );
              setNotify(true);
            })}
        >
          Also tell me when this tab is in the background
        </button>
      )}
    </div>
  );
}
